import React, { useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/useToast';
import Toast from './Toast';

const CallWaiterButton = ({ restaurantId, tableId, customerName, customerMobile }) => {
    const [showModal, setShowModal] = useState(false);
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { toasts, showToast, removeToast } = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;

        // Get customer info from localStorage or props
        const finalName = customerName || localStorage.getItem('customerName') || 'Guest';
        const finalMobile = customerMobile || localStorage.getItem('customerMobile') || 'Not provided';

        setIsSubmitting(true);
        try {
            await axios.post(API_ENDPOINTS.WAITER_REQUESTS, {
                restaurantId,
                tableId,
                customerName: finalName,
                customerMobile: finalMobile,
                message: message.trim()
            });
            
            setMessage('');
            setShowModal(false);
            showToast('Your request has been sent to the restaurant staff!', 'success');
        } catch (error) {
            console.error('Error sending waiter request:', error);
            showToast('Failed to send request. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setShowModal(true)}
                className="fixed bottom-6 right-6 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 z-40 animate-pulse"
            >
                <div className="flex items-center gap-2">
                    <span className="text-2xl">🔔</span>
                    <span className="hidden sm:inline font-semibold">Call Waiter</span>
                </div>
            </button>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-800">Call Waiter</h3>
                            <button 
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    What do you need help with?
                                </label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="e.g., Need extra napkins, want to order dessert, bill please..."
                                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    rows="4"
                                    required
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !message.trim()}
                                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Request'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Toast Notifications */}
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => removeToast(toast.id)}
                />
            ))}
        </>
    );
};

export default CallWaiterButton;